import { useMemo } from "react";

import type { Translate } from "@similarweb/i18n-provider";
import type {
    ProWebsiteFeaturesNoTouchJourneyAddonDiscountType,
    ProWebsiteFeaturesNoTouchJourneyAddonPackageModel,
    ProWebsiteFeaturesNoTouchJourneyAddonPackagesResponse,
    ProWebsiteFeaturesNoTouchJourneyAvailableAddonDiscountModel,
    ProWebsiteFeaturesNoTouchJourneyCountryFilterData,
} from "@similarweb/pro-api-client";

import { useNoTouchPurchasePackagesQuery } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchasePackagesQuery";
import { ADDON_TYPE_NAME_DICT, type AddonTypeName } from "app/contexts/AddonStateContext/const";

import { NO_TOUCH_PURCHASE_TRANSLATIONS } from "../translations";

const getSorter = (addonType: AddonTypeName): ((a: INoTouchAddon, b: INoTouchAddon) => number) => {
    switch (addonType) {
        case ADDON_TYPE_NAME_DICT.CountryFilterDynamic:
            return (a: INoTouchAddon, b: INoTouchAddon) => a.displayName.localeCompare(b.displayName);
        default:
            return (a: INoTouchAddon, b: INoTouchAddon) => a.quantity - b.quantity;
    }
};

export interface INoTouchAddon {
    id: number;
    price: number;
    title: string;
    quantity: number;
    displayName: string;
    productTemplateId: number;
    countryFilterData?: ProWebsiteFeaturesNoTouchJourneyCountryFilterData;
    currency: string;
    availableDiscounts?: ProWebsiteFeaturesNoTouchJourneyAvailableAddonDiscountModel[];
    isPurchased: boolean;
}

interface IPackagesQueryResult {
    selection: INoTouchAddon[];
    currency: string;
}

const getAddonId = (
    addon: ProWebsiteFeaturesNoTouchJourneyAddonPackageModel,
    addonType: string,
    index: number,
): number => {
    switch (addonType) {
        case ADDON_TYPE_NAME_DICT.CountryFilterDynamic:
            return addon.countryFilterData?.countryId ?? index;
        default:
            return addon.quantity ?? 0;
    }
};

interface IGetAddonDisplayNameProps {
    addon: ProWebsiteFeaturesNoTouchJourneyAddonPackageModel;
    addonType: string;
    translate: Translate;
}

const getAddonDisplayName = ({ addon, addonType, translate }: IGetAddonDisplayNameProps): string => {
    switch (addonType) {
        case ADDON_TYPE_NAME_DICT.CountryFilterDynamic:
            return translate(`common.country.${addon.countryFilterData?.countryId}`);
        default:
            return translate(NO_TOUCH_PURCHASE_TRANSLATIONS.summary[addonType].selectText[addon.quantity]);
    }
};

interface IParseQueryDTOProps {
    addonType: string;
    resource: ProWebsiteFeaturesNoTouchJourneyAddonPackagesResponse;
    translate: Translate;
}

const parseQueryDTO = ({ addonType, resource, translate }: IParseQueryDTOProps): IPackagesQueryResult => {
    const aType = addonType === ADDON_TYPE_NAME_DICT.CountryFilterDynamic ? "CountryFilter" : addonType;
    const foundAddonType = resource.addonGroups.find((group) => group.addOnType === aType);
    if (!foundAddonType) {
        return null;
    }

    const addons =
        resource.currentNoTouchPackagePeriod === "monthly"
            ? foundAddonType.addOnPackagesByPeriod.Monthly
            : foundAddonType.addOnPackagesByPeriod.Annually;

    return {
        currency: resource.currency,
        selection: addons
            .map((addon, index): INoTouchAddon => {
                return {
                    price: addon.price,
                    title: addon.package ?? "",
                    id: getAddonId(addon, addonType, index),
                    displayName: getAddonDisplayName({ addon, addonType, translate }),
                    countryFilterData: addon.countryFilterData,
                    quantity: Number(addon.quantity),
                    productTemplateId: Number(addon.productTemplateId),
                    currency: addon.currency ?? "USD",
                    availableDiscounts: addon.availableDiscounts ?? [],
                    isPurchased: addon.isPurchased,
                };
            })
            .sort(getSorter(addonType as AddonTypeName)),
    };
};

export const AvailableDiscountTypes = {
    firstPurchaseDiscount: "FirstPurchaseDiscount",
    singleAddonDiscount: "SingleAddonDiscount",
    bundleAddonDiscount: "BundleAddonDiscount",
} as const satisfies Record<string, ProWebsiteFeaturesNoTouchJourneyAddonDiscountType>;

const getSingleDiscount = (addon: INoTouchAddon) =>
    addon?.availableDiscounts?.find((d) => d.type === AvailableDiscountTypes.singleAddonDiscount);

export const getSingleDiscountValue = (addon: INoTouchAddon) => getSingleDiscount(addon)?.value;

export const getSingleDiscountCode = (addon: INoTouchAddon) => getSingleDiscount(addon)?.code;

interface IUseNoTouchPurchaseSummaryModalProps {
    addonType: AddonTypeName;
    translate: Translate;
}

interface IUseNoTouchPurchaseSummaryModalApi {
    isLoading: boolean;
    parsedData?: IPackagesQueryResult;
}

export const useNoTouchPurchaseSummaryModal = ({
    addonType,
    translate,
}: IUseNoTouchPurchaseSummaryModalProps): IUseNoTouchPurchaseSummaryModalApi => {
    const { isLoading, isError, data } = useNoTouchPurchasePackagesQuery({ addonType });

    const parsedData = useMemo(
        () =>
            isLoading || isError || data === undefined
                ? undefined
                : parseQueryDTO({ addonType, resource: data, translate }),
        [addonType, data, isError, isLoading, translate],
    );

    return {
        isLoading,
        parsedData,
    };
};
